// Copyright 2026 Monterosa Productions Limited. All rights reserved.

// OneTrust's banner / preference centre is injected by a third-party SDK we
// don't control, so we can't theme it through the embed's stylesheet. Instead we
// load the operator-uploaded fonts (configured in project settings — the same
// set the embed renders with) into the parent document via the FontFace API,
// then point the OneTrust rules in index.html at them through the
// --ot-font-family / --ot-heading-font-family CSS variables.

export interface UploadedFont {
  name: string;
  file: string;
  fontWeight?: string;
}

export interface OneTrustFontSettings {
  uploadedFonts?: UploadedFont[];
  // Applied to all non-heading elements.
  bodyFontFamily?: string;
  // Applied to headings (h1–h6).
  labelFontFamily?: string;
}

// Project settings may deliver uploadedFonts as a real array (parent /config
// reads raw JSON) or as a JSON-stringified array. Normalise and validate.
export function parseUploadedFonts(raw: unknown): UploadedFont[] | undefined {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  if (!Array.isArray(value)) return undefined;
  const fonts = value.filter(
    (f): f is UploadedFont =>
      !!f &&
      typeof f === 'object' &&
      typeof (f as UploadedFont).name === 'string' &&
      typeof (f as UploadedFont).file === 'string',
  );
  return fonts.length > 0 ? fonts : undefined;
}

export async function applyOneTrustFonts(settings: OneTrustFontSettings): Promise<void> {
  const fonts = settings.uploadedFonts ?? [];
  if (fonts.length === 0) return;

  // Register each uploaded face and wait for it to download, so OneTrust only
  // gets restyled once the glyphs are ready (no fallback-font flash mid-render).
  await Promise.all(
    fonts.map(async (f) => {
      try {
        const face = new FontFace(f.name, `url(${f.file})`, {
          weight: f.fontWeight ?? '400',
          style: 'normal',
          display: 'swap',
        });
        await face.load();
        document.fonts.add(face);
      } catch (err) {
        console.warn('[parent] failed to load font', f.name, err);
      }
    }),
  );

  // The uploaded faces register as distinct families (e.g. StyryneaRegular /
  // StyryneaBold). Body uses the regular-weight face; headings use the
  // bold-weight face. Explicit operator settings win when present; otherwise
  // fall back to weight, then to the first font.
  const regular = fonts.find((f) => (f.fontWeight ?? '400') === '400') ?? fonts[0];
  const bold = fonts.find((f) => f.fontWeight === '700') ?? regular;
  const body = settings.bodyFontFamily || regular?.name;
  const heading = settings.labelFontFamily || bold?.name;

  const root = document.documentElement;
  if (body) root.style.setProperty('--ot-font-family', body);
  if (heading) root.style.setProperty('--ot-heading-font-family', heading);
}
